import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, Download, Send, Plus, X, Trash2, Upload, Edit3, Check, Minus, MapPin, FileText, ArrowLeft, Wrench, Eye } from 'lucide-react';
import { toast } from 'sonner';
import { API_URL, api } from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import { ServiceIcon, LoadingSpinner } from '../common/Badges';
import { ITEM_ICONS, CATEGORY_COLORS } from '../../utils/constants';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

const BlueprintBuilder = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [catalog, setCatalog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [propertyType, setPropertyType] = useState('residential');
  const [selectedCategory, setSelectedCategory] = useState('electrical');
  const [items, setItems] = useState([]);
  const [projectName, setProjectName] = useState('');
  const [projectAddress, setProjectAddress] = useState('');
  const [clientName, setClientName] = useState('');
  const [generating, setGenerating] = useState(false);
  const [sending, setSending] = useState(false);
  const [floorPlan, setFloorPlan] = useState(null);
  const [placedPins, setPlacedPins] = useState([]);
  const [placingItem, setPlacingItem] = useState(null);
  const [draggingPin, setDraggingPin] = useState(null);
  const blueprintRef = useRef(null);
  const canvasRef = useRef(null);

  useEffect(() => {
    const load = async () => {
      try { const data = await api('/api/services/catalog'); setCatalog(data); }
      catch { toast.error('Failed to load catalog'); }
      finally { setLoading(false); }
    };
    load();
    if (user) { setClientName(user.name || ''); setProjectAddress(user.address || ''); }
  }, [user]);

  const addItem = (category, key, service) => {
    const existing = items.find(i => i.key === key && i.category === category);
    if (existing) {
      setItems(items.map(i => i.key === key && i.category === category ? { ...i, qty: i.qty + 1 } : i));
    } else {
      setItems([...items, { category, key, ...service, qty: 1 }]);
    }
  };

  const placeOnPlan = (category, key, service) => {
    if (!floorPlan) { toast.error('Upload a floor plan first'); return; }
    setPlacingItem({ category, key, ...service });
    toast.info('Click on the floor plan to place this item');
  };

  const handleCanvasClick = (e) => {
    if (!placingItem) return;
    const rect = canvasRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    const newPin = { ...placingItem, x, y, id: Date.now(), scale: 1 };
    setPlacedPins([...placedPins, newPin]);
    addItem(placingItem.category, placingItem.key, { price: placingItem.price, unit: placingItem.unit, description: placingItem.description });
    setPlacingItem(null);
  };

  const handlePinDragStart = (e, pinId) => {
    e.preventDefault();
    e.stopPropagation();
    setDraggingPin(pinId);
  };

  const handleCanvasMouseMove = (e) => {
    if (!draggingPin) return;
    e.preventDefault();
    const rect = canvasRef.current.getBoundingClientRect();
    const x = Math.max(0, Math.min(100, ((e.clientX - rect.left) / rect.width) * 100));
    const y = Math.max(0, Math.min(100, ((e.clientY - rect.top) / rect.height) * 100));
    setPlacedPins(pins => pins.map(p => p.id === draggingPin ? { ...p, x, y } : p));
  };

  const handleCanvasMouseUp = () => { setDraggingPin(null); };

  const resizePin = (pinId, delta) => {
    setPlacedPins(pins => pins.map(p => {
      if (p.id !== pinId) return p;
      const newScale = Math.max(0.1, Math.min(3, (p.scale || 1) + delta));
      return { ...p, scale: newScale };
    }));
  };

  const removePin = (pinId) => {
    const pin = placedPins.find(p => p.id === pinId);
    if (pin) {
      setPlacedPins(placedPins.filter(p => p.id !== pinId));
      const idx = items.findIndex(i => i.key === pin.key && i.category === pin.category);
      if (idx >= 0) {
        const item = items[idx];
        if (item.qty > 1) setItems(items.map((it, i) => i === idx ? { ...it, qty: it.qty - 1 } : it));
        else setItems(items.filter((_, i) => i !== idx));
      }
    }
  };

  const updateQty = (idx, delta) => {
    setItems(items.map((item, i) => i !== idx ? item : { ...item, qty: Math.max(1, item.qty + delta) }));
  };
  const removeItem = (idx) => {
    const item = items[idx];
    setPlacedPins(placedPins.filter(p => !(p.key === item.key && p.category === item.category)));
    setItems(items.filter((_, i) => i !== idx));
  };

  const subtotal = items.reduce((sum, i) => sum + (i.price * i.qty), 0);
  const tax = subtotal * 0.4 * 0.08875;
  const contingency = subtotal * 0.1;
  const grandTotal = subtotal + tax + contingency;

  const downloadPDF = async () => {
    if (!blueprintRef.current) return;
    setGenerating(true);
    try {
      const canvas = await html2canvas(blueprintRef.current, { scale: 2, backgroundColor: '#050505', useCORS: true, allowTaint: true });
      const imgData = canvas.toDataURL('image/jpeg', 0.95);
      const pdf = new jsPDF('p', 'mm', 'a4');
      const w = pdf.internal.pageSize.getWidth();
      const h = (canvas.height * w) / canvas.width;
      let pos = 0;
      const ph = pdf.internal.pageSize.getHeight();
      while (pos < h) { if (pos > 0) pdf.addPage(); pdf.addImage(imgData, 'JPEG', 0, -pos, w, h); pos += ph; }
      pdf.save(`BELLAJA-Blueprint-${projectName || 'Project'}.pdf`);
      toast.success('Blueprint PDF downloaded!');
    } catch { toast.error('PDF failed'); }
    finally { setGenerating(false); }
  };

  const sendToCompany = async () => {
    setSending(true);
    try {
      const itemsList = items.map(i => `${i.description} x${i.qty}${isAdmin ? ` = $${(i.price * i.qty).toFixed(2)}` : ''}`).join('\n');
      const priceInfo = isAdmin ? `\n\nSubtotal: $${subtotal.toFixed(2)}\nNYC Tax: $${tax.toFixed(2)}\nContingency: $${contingency.toFixed(2)}\nTOTAL: $${grandTotal.toFixed(2)}` : '';
      await api('/api/contact', {
        method: 'POST',
        body: JSON.stringify({
          name: clientName || user?.name || 'Blueprint Request',
          email: user?.email || 'noreply@bellajaelectric.com',
          phone: user?.phone || '',
          subject: `Blueprint Estimate: ${projectName || 'New Project'}`,
          message: `PROJECT: ${projectName}\nADDRESS: ${projectAddress}\nCLIENT: ${clientName}\n\nITEMS:\n${itemsList}${priceInfo}\n\nPlaced ${placedPins.length} items on floor plan.`,
          property_type: propertyType
        })
      });
      toast.success('Blueprint sent to BELLAJA! We will contact you shortly.');
    } catch (err) { toast.error('Failed to send. Try again.'); }
    finally { setSending(false); }
  };

  if (loading) return <LoadingSpinner />;

  const services = catalog?.services || {};
  const catData = services[selectedCategory];
  const catServices = catData ? (catData[propertyType] || catData.residential || {}) : {};
  const colors = CATEGORY_COLORS[selectedCategory] || CATEGORY_COLORS.electrical;

  return (
    <div className="space-y-6" data-testid="blueprint-builder">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold text-white">Blueprint Builder</h1>
          <p className="text-sm text-gray-500 mt-1">Upload your floor plan, place items, and get an instant estimate</p>
        </div>
        <div className="flex gap-3 flex-wrap">
          <button onClick={() => navigate(-1)} className="btn-secondary text-sm flex items-center gap-2"><ArrowLeft className="w-4 h-4" />Back</button>
          {items.length > 0 && (
            <>
              <button onClick={sendToCompany} disabled={sending} className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded text-sm font-medium flex items-center gap-2 transition-colors" data-testid="send-blueprint-btn">
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}Send to BELLAJA
              </button>
              <button onClick={downloadPDF} disabled={generating} className="btn-primary flex items-center gap-2" data-testid="download-blueprint-pdf">
                {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}Download PDF
              </button>
            </>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Left Panel: Service Picker */}
        <div className="lg:col-span-3 space-y-4">
          <div className="card p-4">
            <label className="text-xs text-gray-500 font-bold tracking-widest block mb-2">PROPERTY TYPE</label>
            <div className="flex gap-2">
              {['residential', 'commercial'].map(t => (
                <button key={t} onClick={() => setPropertyType(t)} className={`flex-1 py-2 text-sm font-medium rounded transition-all ${propertyType === t ? 'bg-red-600 text-white' : 'bg-gray-800 text-gray-400 hover:bg-gray-700'}`} data-testid={`property-${t}`}>
                  {t === 'residential' ? 'Residential' : 'Commercial'}
                </button>
              ))}
            </div>
          </div>

          <div className="card p-4">
            <label className="text-xs text-gray-500 font-bold tracking-widest block mb-2">CATEGORY</label>
            <div className="grid grid-cols-2 gap-1.5">
              {Object.entries(services).map(([key, cat]) => {
                const c = CATEGORY_COLORS[key] || CATEGORY_COLORS.electrical;
                return (
                  <button key={key} onClick={() => setSelectedCategory(key)}
                    className={`p-2.5 rounded text-left transition-all border ${selectedCategory === key ? `${c.bg} ${c.border} ${c.text}` : 'bg-gray-900 border-gray-800 text-gray-500 hover:border-gray-700'}`}
                    data-testid={`cat-${key}`}>
                    <ServiceIcon type={key} className="w-4 h-4 mb-0.5" />
                    <p className="text-[10px] font-medium leading-tight">{cat.name}</p>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="card overflow-hidden">
            <div className={`p-3 border-b border-gray-800 ${colors.bg}`}>
              <p className={`text-xs font-bold ${colors.text}`}>{catData?.name}</p>
            </div>
            <div className="max-h-[300px] overflow-y-auto divide-y divide-gray-800/50">
              {Object.entries(catServices).map(([key, svc]) => {
                const Icon = ITEM_ICONS[key] || Wrench;
                return (
                  <div key={key} className="p-2.5 flex items-center gap-2 hover:bg-gray-800/50 transition-colors group">
                    <div className={`w-7 h-7 rounded flex items-center justify-center shrink-0 ${colors.bg}`}>
                      <Icon className={`w-3.5 h-3.5 ${colors.icon}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-gray-200 truncate">{svc.description}</p>
                      {isAdmin && <p className="text-[10px] text-gray-600">${svc.price}/{svc.unit}</p>}
                    </div>
                    <div className="flex gap-1 shrink-0">
                      {floorPlan && (
                        <button onClick={() => placeOnPlan(selectedCategory, key, svc)} className="w-6 h-6 rounded bg-blue-900/30 hover:bg-blue-800/50 flex items-center justify-center" title="Place on plan" data-testid={`place-${key}`}>
                          <Eye className="w-3 h-3 text-blue-400" />
                        </button>
                      )}
                      <button onClick={() => addItem(selectedCategory, key, svc)} className="w-6 h-6 rounded bg-gray-800 hover:bg-gray-700 flex items-center justify-center" title="Add to list" data-testid={`add-${key}`}>
                        <Plus className="w-3 h-3 text-gray-400" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Center: Floor Plan Canvas */}
        <div className="lg:col-span-5">
          <div ref={blueprintRef} className="bg-[#050505] border border-gray-800 rounded-lg overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-r from-red-900/40 to-transparent p-6 border-b border-gray-800">
              <div className="flex justify-between items-start">
                <div>
                  <h2 className="text-xl font-bold text-white tracking-wider">BELLAJA</h2>
                  <p className="text-red-500 text-[10px] font-medium tracking-[0.3em]">ELECTRICAL & SECURITY SYSTEMS</p>
                </div>
                <div className="text-right">
                  <p className="text-[10px] text-gray-600">BLUEPRINT</p>
                  <p className="text-[10px] text-gray-400">{new Date().toLocaleDateString()}</p>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4 mt-4">
                <div>
                  <label className="text-[9px] text-red-500/70 font-bold tracking-widest">PROJECT</label>
                  <input value={projectName} onChange={e => setProjectName(e.target.value)} placeholder="Project name..." className="w-full bg-transparent border-b border-gray-800 text-white text-xs py-0.5 focus:outline-none focus:border-red-500 placeholder-gray-700" data-testid="project-name-input" />
                </div>
                <div>
                  <label className="text-[9px] text-red-500/70 font-bold tracking-widest">CLIENT</label>
                  <input value={clientName} onChange={e => setClientName(e.target.value)} placeholder="Client..." className="w-full bg-transparent border-b border-gray-800 text-white text-xs py-0.5 focus:outline-none focus:border-red-500 placeholder-gray-700" data-testid="client-name-input" />
                </div>
                <div>
                  <label className="text-[9px] text-red-500/70 font-bold tracking-widest">LOCATION</label>
                  <input value={projectAddress} onChange={e => setProjectAddress(e.target.value)} placeholder="Address..." className="w-full bg-transparent border-b border-gray-800 text-white text-xs py-0.5 focus:outline-none focus:border-red-500 placeholder-gray-700" data-testid="project-address-input" />
                </div>
              </div>
            </div>

            {/* Floor Plan Area */}
            <div className="p-4">
              {!floorPlan ? (
                <div className="border-2 border-dashed border-gray-700 rounded-lg p-10 text-center hover:border-red-500/50 transition-colors relative cursor-pointer" data-testid="floor-plan-upload">
                  <input type="file" accept="image/*,.heic,.heif,.pdf" className="absolute inset-0 opacity-0 cursor-pointer"
                    onChange={async (e) => {
                      const file = e.target.files[0];
                      if (!file) return;
                      if (file.size > 10 * 1024 * 1024) { toast.error('File too large (max 10MB)'); return; }
                      const token = localStorage.getItem('token');
                      const formData = new FormData();
                      formData.append('file', file);
                      try {
                        const res = await fetch(`${API_URL}/api/upload`, { method: 'POST', headers: { 'Authorization': `Bearer ${token}` }, body: formData });
                        if (!res.ok) throw new Error('Upload failed');
                        const data = await res.json();
                        setFloorPlan({ url: `${API_URL}${data.url}`, localPreview: URL.createObjectURL(file) });
                        toast.success('Floor plan uploaded! Now place items on it.');
                      } catch { setFloorPlan({ localPreview: URL.createObjectURL(file) }); }
                    }}
                  />
                  <Upload className="w-12 h-12 mx-auto mb-3 text-gray-600" />
                  <p className="text-sm text-gray-400 font-medium">Upload Floor Plan / Blueprint</p>
                  <p className="text-xs text-gray-600 mt-1">JPG, PNG, PDF (max 10MB)</p>
                  <p className="text-[10px] text-gray-700 mt-3">Or continue without a plan — just add items from the left</p>
                </div>
              ) : (
                <div
                  ref={canvasRef}
                  className={`relative rounded-lg overflow-hidden border border-gray-800 ${placingItem ? 'cursor-crosshair' : ''}`}
                  onClick={handleCanvasClick}
                  onMouseMove={handleCanvasMouseMove}
                  onMouseUp={handleCanvasMouseUp}
                  onMouseLeave={handleCanvasMouseUp}
                  data-testid="floor-plan-canvas"
                >
                  <img src={floorPlan.localPreview || floorPlan.url} alt="Floor Plan" className="w-full select-none pointer-events-none" draggable={false} />

                  {/* Placed Pins */}
                  {placedPins.map((pin) => {
                    const Icon = ITEM_ICONS[pin.key] || Wrench;
                    const c = CATEGORY_COLORS[pin.category] || CATEGORY_COLORS.electrical;
                    const s = pin.scale || 1;
                    const baseSize = 36;
                    const iconSize = Math.round(baseSize * s);
                    const innerIcon = Math.round(16 * s);
                    return (
                      <div key={pin.id}
                        className={`absolute transform -translate-x-1/2 -translate-y-1/2 group z-10 ${draggingPin === pin.id ? 'z-20' : ''}`}
                        style={{ left: `${pin.x}%`, top: `${pin.y}%` }}
                        onMouseDown={(e) => handlePinDragStart(e, pin.id)}
                        data-testid={`pin-${pin.id}`}
                      >
                        <div className={`rounded-full ${c.bg} border-2 ${c.border} flex items-center justify-center cursor-grab active:cursor-grabbing shadow-lg shadow-black/50 transition-all`}
                          style={{ width: `${iconSize}px`, height: `${iconSize}px` }}>
                          <Icon style={{ width: `${innerIcon}px`, height: `${innerIcon}px` }} className={c.icon} />
                        </div>
                        {/* Tooltip */}
                        <div className="absolute left-1/2 -translate-x-1/2 top-full mt-1 bg-black/90 border border-gray-700 rounded px-2 py-1 whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                          <p className="text-[10px] text-white font-medium">{pin.description}</p>
                          {isAdmin && <p className={`text-[10px] font-bold ${c.text}`}>${pin.price}</p>}
                        </div>
                        {/* Controls: resize + delete */}
                        <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-1 flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button onClick={(e) => { e.stopPropagation(); resizePin(pin.id, -0.1); }}
                            className="w-5 h-5 rounded bg-gray-800 border border-gray-700 flex items-center justify-center hover:bg-gray-700" title="Smaller">
                            <Minus className="w-2.5 h-2.5 text-gray-300" />
                          </button>
                          <span className="text-[8px] text-gray-500 w-7 text-center">{Math.round(s * 100)}%</span>
                          <button onClick={(e) => { e.stopPropagation(); resizePin(pin.id, 0.1); }}
                            className="w-5 h-5 rounded bg-gray-800 border border-gray-700 flex items-center justify-center hover:bg-gray-700" title="Bigger">
                            <Plus className="w-2.5 h-2.5 text-gray-300" />
                          </button>
                          <button onClick={(e) => { e.stopPropagation(); removePin(pin.id); }}
                            className="w-5 h-5 rounded bg-red-900 border border-red-700 flex items-center justify-center hover:bg-red-800 ml-0.5" title="Remove">
                            <X className="w-2.5 h-2.5 text-red-300" />
                          </button>
                        </div>
                      </div>
                    );
                  })}

                  {/* Placing indicator */}
                  {placingItem && (
                    <div className="absolute inset-0 bg-blue-500/5 border-2 border-blue-500/30 rounded flex items-center justify-center pointer-events-none">
                      <p className="bg-blue-900/80 text-blue-300 text-xs px-3 py-1 rounded">Click to place: {placingItem.description}</p>
                    </div>
                  )}
                </div>
              )}
              {floorPlan && (
                <div className="flex items-center justify-between mt-2">
                  <p className="text-[10px] text-gray-600">{placedPins.length} items placed on plan</p>
                  <button onClick={() => { setFloorPlan(null); setPlacedPins([]); }} className="text-[10px] text-red-500 hover:text-red-400">Remove Plan</button>
                </div>
              )}
            </div>

            {/* Price Summary - Admin Only */}
            {isAdmin && items.length > 0 && (
              <div className="p-6 border-t border-gray-800">
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between"><span className="text-gray-500">Subtotal ({items.reduce((s, i) => s + i.qty, 0)} items)</span><span className="text-white">${subtotal.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span></div>
                  <div className="flex justify-between"><span className="text-gray-500">NYC Sales Tax (8.875%)</span><span className="text-white">${tax.toFixed(2)}</span></div>
                  <div className="flex justify-between"><span className="text-gray-500">Contingency (10%)</span><span className="text-white">${contingency.toFixed(2)}</span></div>
                  <div className="flex justify-between pt-3 border-t border-gray-700">
                    <span className="text-red-500 font-bold">TOTAL ESTIMATE</span>
                    <span className="text-white font-bold text-xl" data-testid="blueprint-total">${grandTotal.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                  </div>
                </div>
              </div>
            )}

            {/* Item Count for Customer */}
            {!isAdmin && items.length > 0 && (
              <div className="p-6 border-t border-gray-800">
                <div className="flex items-center justify-between">
                  <span className="text-gray-400 text-sm">{items.reduce((s, i) => s + i.qty, 0)} services selected</span>
                  <span className="text-xs text-gray-600">Send to BELLAJA for a detailed quote</span>
                </div>
              </div>
            )}

            <div className="p-4 border-t border-gray-800 bg-gray-900/30">
              <p className="text-[10px] text-gray-600 text-center">{isAdmin ? 'Prices are estimates. Final pricing may vary. 1-year labor warranty included.' : 'Upload your floor plan and select the services you need. Send to BELLAJA for a professional quote.'}</p>
            </div>
          </div>
        </div>

        {/* Right: Items List */}
        <div className="lg:col-span-4">
          <div className="card overflow-hidden sticky top-4">
            <div className="p-4 border-b border-gray-800 flex items-center justify-between">
              <h3 className="text-sm font-bold text-white">Selected Items</h3>
              <span className="text-xs text-gray-500">{items.length} services</span>
            </div>
            <div className="max-h-[500px] overflow-y-auto divide-y divide-gray-800/50">
              {items.length === 0 ? (
                <div className="p-8 text-center">
                  <Wrench className="w-8 h-8 mx-auto mb-2 text-gray-700" />
                  <p className="text-xs text-gray-600">Add services from the catalog</p>
                </div>
              ) : (
                Object.entries(
                  items.reduce((groups, item) => {
                    (groups[item.category] = groups[item.category] || []).push(item);
                    return groups;
                  }, {})
                ).map(([cat, catItems]) => {
                  const c = CATEGORY_COLORS[cat] || CATEGORY_COLORS.electrical;
                  return (
                    <div key={cat}>
                      <div className={`px-4 py-2 ${c.bg} flex items-center gap-2`}>
                        <ServiceIcon type={cat} className={`w-3.5 h-3.5 ${c.icon}`} />
                        <p className={`text-[10px] font-bold tracking-widest uppercase ${c.text}`}>{services[cat]?.name}</p>
                      </div>
                      {catItems.map((item) => {
                        const globalIdx = items.indexOf(item);
                        const Icon = ITEM_ICONS[item.key] || Wrench;
                        return (
                          <div key={globalIdx} className="p-3 flex items-center gap-3" data-testid={`blueprint-item-${globalIdx}`}>
                            <div className={`w-8 h-8 rounded flex items-center justify-center shrink-0 ${c.bg}`}>
                              <Icon className={`w-4 h-4 ${c.icon}`} />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-xs text-white truncate">{item.description}</p>
                              {isAdmin && <p className="text-[10px] text-gray-600">${item.price}/{item.unit}</p>}
                            </div>
                            <div className="flex items-center gap-1.5 shrink-0">
                              <button onClick={() => updateQty(globalIdx, -1)} className="w-6 h-6 rounded bg-gray-800 hover:bg-gray-700 flex items-center justify-center"><Minus className="w-3 h-3 text-gray-400" /></button>
                              <span className="text-white font-bold text-xs w-5 text-center">{item.qty}</span>
                              <button onClick={() => updateQty(globalIdx, 1)} className="w-6 h-6 rounded bg-gray-800 hover:bg-gray-700 flex items-center justify-center"><Plus className="w-3 h-3 text-gray-400" /></button>
                            </div>
                            {isAdmin && <p className="text-white font-bold text-xs w-16 text-right">${(item.price * item.qty).toLocaleString()}</p>}
                            <button onClick={() => removeItem(globalIdx)} className="p-1 hover:bg-red-900/30 rounded"><Trash2 className="w-3.5 h-3.5 text-gray-600 hover:text-red-400" /></button>
                          </div>
                        );
                      })}
                    </div>
                  );
                })
              )}
            </div>
            {items.length > 0 && (
              <div className="p-4 border-t border-gray-800 bg-gray-900/50">
                {isAdmin ? (
                  <>
                    <div className="flex justify-between mb-2 text-xs"><span className="text-gray-500">Subtotal</span><span className="text-white">${subtotal.toFixed(2)}</span></div>
                    <div className="flex justify-between mb-2 text-xs"><span className="text-gray-500">Tax + Contingency</span><span className="text-white">${(tax + contingency).toFixed(2)}</span></div>
                    <div className="flex justify-between pt-2 border-t border-gray-700">
                      <span className="text-red-500 font-bold text-sm">TOTAL</span>
                      <span className="text-white font-bold text-lg">${grandTotal.toFixed(2)}</span>
                    </div>
                  </>
                ) : (
                  <div className="text-center">
                    <p className="text-xs text-gray-500">{items.reduce((s, i) => s + i.qty, 0)} items selected</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};


export default BlueprintBuilder;
